import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageHeader from "@/components/feature/PageHeader";
import WorkCard from "@/components/base/WorkCard";
import EmptyState from "@/components/base/EmptyState";
import { works } from "@/mocks/works";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 8;

const allCategories = Array.from(new Set(works.map((w) => w.category)));
const allYears = Array.from(new Set(works.map((w) => w.year))).sort((a, b) => b - a);
const allStatuses = Array.from(new Set(works.map((w) => w.status)));

export default function WorksPage() {
  useSeo({
    title: "天海翼影视作品全集 — 天海翼影视内容站",
    description:
      "天海翼影视作品全集，按作品类型、上映年份与作品状态筛选，支持按发布时间与热度排序，快速浏览天海翼的电视剧、电影与纪实作品。",
    keywords: "天海翼作品,天海翼影视剧,天海翼全集,影视作品列表,天海翼电视剧,天海翼电影",
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const [category, setCategory] = useState(searchParams.get("category") || "全部");
  const [year, setYear] = useState("全部");
  const [status, setStatus] = useState("全部");
  const [sort, setSort] = useState<"new" | "hot">("new");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [showFilters, setShowFilters] = useState(false);

  const filtered = useMemo(() => {
    const list = works.filter((w) => {
      if (category !== "全部" && w.category !== category) return false;
      if (year !== "全部" && String(w.year) !== year) return false;
      if (status !== "全部" && w.status !== status) return false;
      return true;
    });
    return list.sort((a, b) => {
      if (sort === "hot") return b.popularity - a.popularity;
      return b.releaseDate.localeCompare(a.releaseDate);
    });
  }, [category, year, status, sort]);

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  const resetFilters = () => {
    setCategory("全部");
    setYear("全部");
    setStatus("全部");
    setSort("new");
    setVisible(PAGE_SIZE);
    setSearchParams({});
  };

  const updateCategory = (value: string) => {
    setCategory(value);
    setVisible(PAGE_SIZE);
    if (value === "全部") {
      searchParams.delete("category");
      setSearchParams(searchParams);
    } else {
      setSearchParams({ category: value });
    }
  };

  const chipClass = (active: boolean) =>
    `cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
      active
        ? "bg-primary-500 text-background-50"
        : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
    }`;

  return (
    <>
      <PageHeader
        title="影视作品全集"
        description="收录天海翼的电影、电视剧与纪实作品。通过下方的类型、年份与状态筛选，快速定位你想了解的作品。"
        crumbs={[{ label: "首页", path: "/" }, { label: "影视作品" }]}
      />

      <section className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
        <div className="mb-6 flex items-center justify-between gap-4">
          <h2 className="font-heading text-lg font-semibold text-foreground-950">
            共 <span className="text-primary-700">{filtered.length}</span> 部作品
          </h2>
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            className="flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 md:hidden"
          >
            <i className="ri-filter-3-line" />
            {showFilters ? "收起筛选" : "筛选条件"}
          </button>
        </div>

        <div className={`${showFilters ? "block" : "hidden"} mb-8 rounded-lg border border-background-200 bg-background-100 p-5 md:mb-10 md:block`}>
          <div className="flex flex-col gap-5">
            <div>
              <p className="mb-3 text-xs font-medium text-foreground-500">作品类型</p>
              <div className="flex flex-wrap gap-2">
                {["全部", ...allCategories].map((cat) => (
                  <button key={cat} type="button" onClick={() => updateCategory(cat)} className={chipClass(category === cat)}>
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-foreground-500">上映年份</span>
                <select
                  value={year}
                  onChange={(e) => {
                    setYear(e.target.value);
                    setVisible(PAGE_SIZE);
                  }}
                  className="h-10 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none focus:border-primary-400"
                >
                  <option value="全部">全部年份</option>
                  {allYears.map((y) => (
                    <option key={y} value={String(y)}>
                      {y} 年
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-foreground-500">作品状态</span>
                <select
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value);
                    setVisible(PAGE_SIZE);
                  }}
                  className="h-10 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none focus:border-primary-400"
                >
                  <option value="全部">全部状态</option>
                  {allStatuses.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>

              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-foreground-500">排序方式</span>
                <div className="flex h-10 items-center gap-1 rounded-md border border-background-300 bg-background-50 p-1">
                  <button
                    type="button"
                    onClick={() => setSort("new")}
                    className={`flex-1 cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      sort === "new" ? "bg-primary-500 text-background-50" : "text-foreground-700"
                    }`}
                  >
                    最新发布
                  </button>
                  <button
                    type="button"
                    onClick={() => setSort("hot")}
                    className={`flex-1 cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      sort === "hot" ? "bg-primary-500 text-background-50" : "text-foreground-700"
                    }`}
                  >
                    热度最高
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            icon="ri-movie-line"
            title="没有找到符合条件的作品"
            description="试试调整筛选条件，或重置后浏览全部影视作品。"
            actionText="重置筛选条件"
            onAction={resetFilters}
          />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 md:gap-5">
              {shown.map((work, index) => (
                <WorkCard key={work.id} work={work} index={index} />
              ))}
            </div>

            {hasMore && (
              <div className="mt-10 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-8 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  加载更多作品
                  <i className="ri-arrow-down-line ml-1 align-middle" />
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}